/**
 * Error registry — the only codes the API may return (docs/3-backend.md §5). Adding a code means adding it
 * to the doc first, then here, then `errors.<CODE>` in fr.json and ar.json (checked by pnpm i18n:check).
 */
const REGISTRY = {
  VALIDATION_FAILED: 400,
  UNAUTHENTICATED: 401,
  TOKEN_EXPIRED: 401,
  INVALID_CREDENTIALS: 401,
  LOGIN_LOCKED: 429,
  EMAIL_ALREADY_USED: 409,
  RESET_TOKEN_INVALID: 410,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  APP_VERSION_TOO_OLD: 426,
  RATE_LIMITED: 429,
  INVITE_INVALID: 410,
  INVITE_EXPIRED: 410,
  ALREADY_STAFF: 409,
  MERCHANT_INACTIVE: 409,
  /** ADR 0007 D5: the shop's status does not allow this Ops action (`details.status`) */
  MERCHANT_STATUS_INVALID: 409,
  QR_INVALID: 422,
  QR_EXPIRED: 422,
  MEMBER_CODE_INVALID: 422,
  MEMBER_NOT_FOUND: 404,
  CUSTOMER_BLOCKED: 409,
  OUTSIDE_HOURS: 409,
  PROGRAM_REQUIRED: 409,
  PROGRAM_NOT_ACTIVE: 409,
  SELF_EARN_FORBIDDEN: 409,
  EARN_TOO_SOON: 409,
  EARN_DAILY_CAP: 409,
  MEMBER_CODE_EARN_LIMIT: 409,
  AMOUNT_NEEDS_MANAGER: 409,
  UNDO_WINDOW_PASSED: 409,
  INSUFFICIENT_BALANCE: 409,
  NO_REWARD_AVAILABLE: 409,
  OUT_OF_STOCK: 409,
  REWARD_INACTIVE: 409,
  CANCEL_WINDOW_PASSED: 409,
  VOUCHER_USED: 409,
  VOUCHER_EXPIRED: 409,
  VOUCHER_CANCELLED: 409,
  VOUCHER_WRONG_MERCHANT: 409,
  VOUCHER_NOT_FOUND: 404,
  VOID_WINDOW_PASSED: 409,
  VOID_WOULD_GO_NEGATIVE: 409,
  ALREADY_VOIDED: 409,
  STOCK_BELOW_RESERVED: 409,
  PROGRAM_ENDING: 409,
  PROGRAM_ENDED: 409,
  PROGRAM_HAS_MEMBERS: 409,
  /** ADR 0005: at most 2 running (ACTIVE or PAUSED) programs per shop */
  PROGRAM_LIMIT_REACHED: 409,
  PROMO_LOCKED: 409,
  MERCHANT_FROZEN: 402,
  CONFLICT_RETRY: 409,
  /** ADR 0009 D5: the account owns a shop that is not closed */
  ACCOUNT_OWNS_SHOP: 409,
  /** ADR 0011 D1: the account already works in a shop that is not closed (one person, one shop) */
  ALREADY_IN_A_SHOP: 409,
  /** ADR 0012 D3: the role already has its 2 places (members and open invitation links) */
  TEAM_LIMIT_REACHED: 409,
  INTERNAL: 500,
} as const satisfies Record<string, number>;

export type ErrorCode = keyof typeof REGISTRY;

export const ERROR_CODES = Object.keys(REGISTRY) as ErrorCode[];

export function isErrorCode(value: string): value is ErrorCode {
  return Object.prototype.hasOwnProperty.call(REGISTRY, value);
}

export function httpStatusOf(code: ErrorCode): number {
  return REGISTRY[code];
}

export type ErrorDetails = Record<string, unknown>;

/** The only error business code throws; the API error filter maps it to `{ error: { code, … } }`. */
export class DomainError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: ErrorDetails;

  constructor(code: ErrorCode, details: ErrorDetails = {}) {
    super(code);
    this.name = 'DomainError';
    this.code = code;
    this.status = REGISTRY[code];
    this.details = details;
  }
}

/** Shape of every error response body. */
export interface ErrorBody {
  error: { code: ErrorCode; message: string; requestId: string; details: ErrorDetails };
}
